import type { Brand } from '@axo/config/@types/Brand.types';
import { ConfigService } from '@axo/config/ConfigService';
import { AnalyticsProvider } from '@axo/shared/services/analytics';
import { useDataLayerConsentObserver } from '@axo/shared/services/google-tag-manager';
import { ToastProvider } from '@axo/ui-core/components/Toast';
import { ThemeProvider } from '@axo/ui-core/themes';
import { basicLogger, LDProvider } from 'launchdarkly-react-client-sdk';
import { ReactNode } from 'react';
import { useSetUtmParametersFromAffiliateTrackingLinkOnMount } from './@utils/useSetUtmParametersFromAffiliateTrackingLinkOnMount.v2';

export type WhiteLabelWrapperOptions = {
  brand: Brand;
};

export const WhiteLabelWrapper = ({
  children,
}: WhiteLabelWrapperOptions & { children: ReactNode }) => {
  const brand = ConfigService.get('global').brand;
  const country = ConfigService.get('global').country.toLocaleLowerCase();
  const theme = ConfigService.get('site').theme.name;

  useDataLayerConsentObserver();

  useSetUtmParametersFromAffiliateTrackingLinkOnMount({
    integrationUrl: import.meta.env.VITE_INTEGRATION_URL,
  });

  return (
    <AnalyticsProvider enabledServices={['gtag']}>
      <LDProvider
        clientSideID={import.meta.env.VITE_LAUNCH_DARKLY_CLIENT}
        context={{
          kind: 'multi',
          runtime: {
            key: 'white-label',
          },
          brand: {
            key: brand,
          },
          country: {
            key: country,
          },
          host: {
            key: window.location.hostname,
          },
          // backwards compatibility
          'form-v2': {
            key: `${country}-wl`,
            country: country,
          },
        }}
        reactOptions={{ useCamelCaseFlagKeys: false }}
        options={{
          logger: basicLogger({
            level: import.meta.env.DEV ? 'info' : 'warn',
          }),
        }}
      >
        <ThemeProvider theme={theme}>
          <ToastProvider>{children}</ToastProvider>
        </ThemeProvider>
      </LDProvider>
    </AnalyticsProvider>
  );
};
